import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Store } from "vuex";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

export interface User {
  userId: string;
  email: string;
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  picture: string;
  phoneNumber: string;
  userRoles: Array<string>;
  dateOfBirth: string;
  lastLogin: string;
}

export interface Token {
  authToken: string;
  refreshToken: string;
  expiresIn: string;
}

export interface UserAuthInfo {
  errors: Array<string>;
  token: Token;
  isAuthenticated: boolean;
}

export interface ContactInfo {
  email: string;
  fullname: string;
  subject: string;
  contact_description: string;
}

@Module
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = [];
  token = {} as Token;
  user = {} as User;
  isAuthenticated = !!JwtService.getToken();

  /**
   * Get current user object
   * @returns Token
   */
  get currentToken(): Token {
    return this.token;
  }

  /**
   * Get current user object
   * @returns User
   */
  get currentUser(): User {
    return this.user;
  }

  /**
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Get authentification errors
   * @returns array
   */
  get getErrors(): Array<string> {
    return this.errors;
  }

  @Mutation
  [Mutations.SET_ERROR](error) {
    this.errors = error;
  }

  @Mutation
  [Mutations.SET_AUTH](token) {
    this.isAuthenticated = true;
    this.token = token;
    this.errors = [];
    JwtService.saveToken(this.token.authToken);
  }

  @Mutation
  [Mutations.SET_USER](user) {
    this.user = user;
  }

  @Mutation
  [Mutations.PURGE_AUTH](): void {
    this.isAuthenticated = false;
    this.token = {} as Token;
    this.user = {} as User;
    this.errors = [];
    JwtService.destroyToken();
  }

  @Action
  [Actions.LOGIN](credentials) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("auth/login", credentials)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);
          resolve();
        })
        .catch(({ response }) => {
          console.log(response);
          this.context.commit(Mutations.SET_ERROR, [response.data]);
          reject();
        });
    });
  }

  @Action
  [Actions.LOGOUT]() {
    this.context.commit(Mutations.PURGE_AUTH);
  }

  @Action
  [Actions.REGISTER](credentials) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("auth/register", credentials)
        .then(() => {
          resolve();
        })
        .catch(({ response }) => {
          console.log(response);
          this.context.commit(Mutations.SET_ERROR, [response.data]);
          reject();
        });
    });
  }

  @Action
  [Actions.FORGOT_PASSWORD](payload) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("auth/forgotpassword", payload)
        .then(() => {
          resolve();
        })
        .catch(({ response }) => {
          console.log(response.data.errors);
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }

  @Action
  [Actions.VERIFY_AUTH]() {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.get("profile")
        .then(({ data }) => {
          console.log(data);
          this.context.commit(Mutations.SET_USER, data.data);
        })
        .catch(({ response }) => {
          console.log(response);
          // this.context.commit(Mutations.SET_ERROR, ["Error"]);
          this.context.commit(Mutations.PURGE_AUTH);
        });
    } else {
      this.context.commit(Mutations.PURGE_AUTH);
    }
  }

  @Action
  [Actions.UPDATE_USER](payload) {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.post("update_user", payload)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_USER, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }
  @Action
  [Actions.CONTACT](ContactInfo) {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.post("contact", ContactInfo)
        .then(({ data }) => {
          resolve();
        })
        .catch(({ response }) => {
          console.log(response)
          this.context.commit(Mutations.SET_ERROR, [response.data]);
          reject();
        });
    });
  }
  @Action
  [Actions.UPDATE_PROFILE](credentials) {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.post("profile/update_profile", credentials)
        .then(({ data }) => {
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, [response.data]);
          reject();
        });
    });
  }
  @Action
  [Actions.UPDATE_EMAIL](credentials) {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.post("auth/update_email", credentials)
        .then(({ data }) => {
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, [response.data]);
          reject();
        });
    });
  }
  @Action
  [Actions.UPDATE_PASSWORD](credentials) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("auth/update_password", credentials)
        .then(() => {
          resolve();
        })
        .catch(({ response }) => {
          console.log(response.data.errors);
          this.context.commit(Mutations.SET_ERROR, [response.data]);
          reject();
        });
    });
  }
}
